import React, { useState } from "react";
import axios from "../../utils/axiosConfig";
import {
  Box,
  Typography,
  Button,
  Stack,
  Alert,
  Snackbar,
  CircularProgress,
  Divider
} from "@mui/material";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import SyncIcon from "@mui/icons-material/Sync";
import PersonOffIcon from "@mui/icons-material/PersonOff";
import { handleApiError } from "../../utils/apiErrorHandler";
import ExportCompetitionCardsButton from "./ExportCompetitionCardsButton";
import ExportSporttechButton from "./ExportSporttechButton";

interface ImportExportSyncPanelProps {
  competitionId: string;
  competitionName: string;
}

const ImportExportSyncPanel: React.FC<ImportExportSyncPanelProps> = ({ competitionId, competitionName }) => {
  const [loading, setLoading] = useState(false);
  const [syncLoading, setSyncLoading] = useState(false);
  const [sync2Loading, setSync2Loading] = useState(false);
  const [withdrawnLoading, setWithdrawnLoading] = useState(false);
  const [syncSuccess, setSyncSuccess] = useState(false);
  const [sync2Success, setSync2Success] = useState(false);
  const [withdrawnSuccess, setWithdrawnSuccess] = useState(false);
  const [syncError, setSyncError] = useState<string | null>(null);

  const handleExportCSV = async () => {
    try {
      setLoading(true);
      
      // Make API call to export data
      const response = await axios.get(`/api/sporttech-export/export-csv/${competitionId}`, {
        responseType: 'blob' // Important for file downloads
      });
      
      // Create a URL for the blob
      const url = window.URL.createObjectURL(new Blob([response.data]));
      
      // Create a link and trigger download
      const link = document.createElement('a');
      link.href = url;
      const filename = `sporttech-export-${competitionId}.csv`;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      
      // Clean up
      window.URL.revokeObjectURL(url);
      document.body.removeChild(link);
    } catch (error) {
      handleApiError(error, "Fehler beim Exportieren der CSV-Datei.");
    } finally {
      setLoading(false);
    }
  };

  const handleSyncStartTimes = async () => {
    try {
      setSyncLoading(true);
      setSyncError(null);
      
      // Make API call to sync start times
      await axios.get(`/api/sporttech/sync-athletes-startdata/${competitionId}`);
      
      // If successful, show success message
      setSyncSuccess(true);
    } catch (error) {
      handleApiError(error, "Fehler beim Synchronisieren der Startzeiten.");
      setSyncError("Fehler beim Synchronisieren der Startzeiten.");
    } finally {
      setSyncLoading(false);
    }
  };

  const handleSyncCompulsoryRoutines = async () => {
    try {
      setSync2Loading(true);
      setSyncError(null);

      const response = await axios.post(`/api/sporttech/sync-exercises/${competitionId}`);

      setSync2Success(true);

      if (response.data.results) {
        const { successful, failed, total, errors } = response.data.results;
        console.log(`Pflichtübungen synchronisiert: ${successful}/${total} erfolgreich, ${failed} fehlgeschlagen`);
        const unmapped = errors?.filter((e: any) => e.athleteGUID && e.athleteName);
        if (unmapped?.length > 0) {
          console.warn(`Nicht gemappte Sportler: ${unmapped.map((e: any) => e.athleteName).join(', ')}`);
        }
      }
    } catch (error) {
      handleApiError(error, "Fehler beim Synchronisieren der Pflichtübungen.");
      setSyncError("Fehler beim Synchronisieren der Pflichtübungen.");
    } finally {
      setSync2Loading(false);
    }
  };

  const handleMarkWithdrawnAthletes = async () => {
    try {
      setWithdrawnLoading(true);
      setSyncError(null);
      
      // Make API call to mark withdrawn athletes
      const response = await axios.post(`/api/sporttech/mark-withdrawn-athletes/${competitionId}`);
      
      // If successful, show success message
      setWithdrawnSuccess(true);
      
      // Optionally show detailed results
      if (response.data.results) {
        const { successful, failed, total } = response.data.results;
        console.log(`Abgemeldete Sportler markiert: ${successful}/${total} erfolgreich, ${failed} fehlgeschlagen`);
      }
    } catch (error) {
      handleApiError(error, "Fehler beim Markieren der abgemeldeten Sportler.");
      setSyncError("Fehler beim Markieren der abgemeldeten Sportler.");
    } finally {
      setWithdrawnLoading(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSyncSuccess(false);
    setSync2Success(false);
    setWithdrawnSuccess(false);
  };

  return (
    <Box sx={{ p: 3, minHeight: '300px' }}>
      <Typography variant="h6" gutterBottom>
        Sporttech Integration
      </Typography>
      
      <Typography variant="body2" color="text.secondary" paragraph>
        Hier können Daten mit Startzeiten mit Sporttech synchronisiert und exportiert werden.
      </Typography>
      
      <Stack spacing={3} direction="column" sx={{ mt: 3 }}>
        <Button
          variant="contained"
          startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <FileDownloadIcon />}
          onClick={handleExportCSV}
          disabled={loading || !competitionId}
        >
          {loading ? "Exportiere..." : "Meldungen CSV Exportieren"}
        </Button>
        
        <Button
          variant="contained"
          startIcon={syncLoading ? <CircularProgress size={20} color="inherit" /> : <SyncIcon />}
          onClick={handleSyncStartTimes}
          disabled={syncLoading || !competitionId}
          color="secondary"
        >
          {syncLoading ? "Synchronisiere..." : "Startzeiten Synchronisieren"}
        </Button>
        
        <Button
          variant="contained"
          startIcon={sync2Loading ? <CircularProgress size={20} color="inherit" /> : <SyncIcon />}
          onClick={handleSyncCompulsoryRoutines}
          disabled={sync2Loading || !competitionId}
          color="warning"
        >
          {sync2Loading ? "Synchronisiere..." : "Pflichtübungen Synchronisieren (nur mit lokalem Server)"}
        </Button>
        
        <Button
          variant="contained"
          startIcon={withdrawnLoading ? <CircularProgress size={20} color="inherit" /> : <PersonOffIcon />}
          onClick={handleMarkWithdrawnAthletes}
          disabled={withdrawnLoading || !competitionId}
          color="error"
        >
          {withdrawnLoading ? "Markiere..." : "Abgemeldete Sportler markieren (nur mit lokalem Server)"}
        </Button>
        
        {syncError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {syncError}
          </Alert>
        )}
      </Stack>

      <Divider sx={{ mt: 3, mb: 3 }} />

      {/* <Typography variant="h6" gutterBottom>
        Sporttech-Startgruppen-Daten Export
      </Typography> */}

      <Typography variant="body2" color="text.secondary" paragraph>
        Hier können die Sporttech-Startgruppen-Daten exportiert und in die externe Datenbank übertragen werden.
      </Typography>

      <Stack spacing={3} direction="column" sx={{ mt: 3, mb: 3 }}>
        <ExportSporttechButton 
          competitionId={competitionId} 
          competitionName={competitionName} 
        />
      </Stack>

      <Divider sx={{ mt: 3, mb: 3 }} />

      <Typography variant="h6" gutterBottom>
        Wettkampfkarten Export
      </Typography>
      
      <Typography variant="body2" color="text.secondary" paragraph>
        Hier können die Wettkampfkarten exportiert werden.
      </Typography>
      
      <Stack spacing={3} direction="column" sx={{ mt: 3 }}>
        <ExportCompetitionCardsButton 
          competitionId={competitionId} 
          competitionName={competitionName} 
        />
      </Stack>
      
      <Snackbar 
        open={syncSuccess} 
        autoHideDuration={6000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity="success">
          Startzeiten wurden erfolgreich synchronisiert!
        </Alert>
      </Snackbar>
      
      <Snackbar 
        open={sync2Success} 
        autoHideDuration={6000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity="success">
          Pflichtübungen wurden erfolgreich synchronisiert!
        </Alert>
      </Snackbar>
      
      <Snackbar 
        open={withdrawnSuccess} 
        autoHideDuration={6000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity="success">
          Abgemeldete Sportler wurden erfolgreich markiert!
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ImportExportSyncPanel;